import { IRulerInfo } from '@/editor/entities/2d/rulerModel';
import { IRulerGuideLineInfo } from '@/editor/entities/2d/rulerGuideLineModel';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType, EFaceSide, IWallEdge } from '@/editor/types';
import { mergeCollinearLoopEdges } from '@/editor/utils/edge';
import { genLineSegHash } from '@/editor/utils/hash';
import { getArcWallRulerInfos } from '@/editor/utils/ruler/wall';
import { isArcWallData } from '@/editor/data/wall/utils';
import { isArcSeparateWallData } from '@/editor/data/separateWall/utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { DEFAULT_RULER_OFFSET } from '@/editor/constant/ruler';

/**
 * 基于 store 数据收集墙体/隔墙/分区标尺信息，与运行时渲染无关。
 * 运行时标尺渲染（WallRulerManager）与 PDF/DXF 导出共用，保证收集逻辑一致。
 */
export function collectWallRulerInfos(globalStores: TGlobalStores): {
  rulerInfos: IRulerInfo[];
  chordGuideLines: IRulerGuideLineInfo[];
} {
  const regionEdges: IRulerInfo[] = [];

  // 房间标尺
  const roomWallIdSet = new Set<string>(); // 房间墙体id集合
  globalStores.connectedWallOutlines
    .filter((outline) => !outline.outest)
    .forEach((outline) => {
      outline.edges.forEach((edge) => {
        roomWallIdSet.add(edge.wallId);
      });
    });

  // 弧形墙 id 集合 & 墙厚映射（用于过滤弧墙接头处的相切碎屑边）
  const arcWallIdSet = new Set<string>();
  const wallThicknessMap = new Map<string, number>();
  globalStores.wallStore.getWallDatas().forEach((wallData) => {
    wallThicknessMap.set(wallData.id, wallData.baseData.thickness);
    if (isArcWallData(wallData)) arcWallIdSet.add(wallData.id);
  });

  // 普通直墙标尺
  const wallEdges = globalStores.connectedWallOutlines
    .filter((outline) => outline.outest)
    .flatMap((outline) => {
      const { edges } = outline;
      return edges.filter((edge, i) => {
        if (roomWallIdSet.has(edge.wallId) || edge.side !== EFaceSide.MainLeftSide || edge.center) return false;

        // 弧形墙与直墙近相切相接时，会在直墙外轮廓上残留一小段长度远小于墙厚的"碎屑边"，
        // 它并非真实可标注尺寸。当这样的短边在轮廓环上紧邻弧形墙的边时，跳过、不生成标尺。
        const thickness = wallThicknessMap.get(edge.wallId) ?? 0;
        if (Point2dUtils.distanceTo(edge.start, edge.end) < thickness) {
          const prev = edges[(i - 1 + edges.length) % edges.length];
          const next = edges[(i + 1) % edges.length];
          if (arcWallIdSet.has(prev.wallId) || arcWallIdSet.has(next.wallId)) return false;
        }

        return true;
      });
    })
    .map((edge) => {
      const rulerHash = genLineSegHash(edge.start, edge.end);
      return {
        id: `wall-${edge.wallId}-${rulerHash}`,
        start: edge.start,
        end: edge.end
      };
    });

  // 弧形墙标尺
  const arcRulerInfos: IRulerInfo[] = [];
  const arcChordGuideLines: IRulerGuideLineInfo[] = [];
  globalStores.wallStore.getWallDatas().forEach((wallData) => {
    if (!isArcWallData(wallData)) return;
    const _arcRulerInfos = getArcWallRulerInfos(wallData.baseData);
    arcRulerInfos.push(_arcRulerInfos.sagittaRuler);

    // 弦长标尺画在弦上，与房间轮廓那一侧的标尺方向一致、数值只差墙厚，属于重复标注。
    // 因此弧形墙已围成房间时只保留弦高标尺，跨度交给房间/分区标尺表达。
    if (!roomWallIdSet.has(wallData.id)) {
      arcRulerInfos.push(_arcRulerInfos.chordRuler);
    }

    // 弦高标尺自弦中点量到弧中点，弦的虚线是它的基准线，弦长标尺是否显示都要保留
    arcChordGuideLines.push(_arcRulerInfos.chordRuler);
  });

  // 分区标尺
  const regions = globalStores.regionStore.getRegionDatas();
  const sepWallThickness = globalStores.paramStore.separateWallThickness;
  const connectedSepWallIdSet = new Set<string>(); // 用于后面排除非自由隔墙
  const existingRulerHashSet = new Set<number>(); // 用于排除重复标尺
  for (const region of regions) {
    const pathInfoList = [region.corePathInfos, ...region.coreHoleInfos];
    for (const pathInfos of pathInfoList) {
      const validPathInfos: typeof pathInfos = [];
      for (let i = 0; i < pathInfos.length; i++) {
        const pathInfo = pathInfos[i];
        if (!pathInfo.sepId) continue;
        connectedSepWallIdSet.add(pathInfo.sepId);
        // 如果是弧形隔墙，则不显示分区标尺，因为分区隔墙有自己的弦长标尺
        // 起点/终点侧面（StartSide/EndSide）是墙体端头的封口边，长度等于墙厚，仅用于几何闭合，不代表实际尺寸，不应显示标尺
        // （自由弧墙两端悬空时会各产生一条这样的端头封口边，若不排除会显示出等于墙厚的多余标注）
        if (pathInfo.center || pathInfo.side === EFaceSide.StartSide || pathInfo.side === EFaceSide.EndSide) {
          continue;
        }

        // 弧形隔墙与相邻直隔墙近相切相接时，接头处会残留一小段长度远小于隔墙厚度的"碎屑边"，
        // 它并非真实可标注尺寸（渲染后四舍五入显示为 0）。当这样的短边在分区环上紧邻弧形隔墙时，跳过、不生成标尺。
        if (Point2dUtils.distanceTo(pathInfo.start, pathInfo.end) < sepWallThickness) {
          const prev = pathInfos[(i - 1 + pathInfos.length) % pathInfos.length];
          const next = pathInfos[(i + 1) % pathInfos.length];
          if (prev.center || next.center) continue;
        }

        validPathInfos.push(pathInfo);
      }

      // 合并共线线段
      mergeCollinearLoopEdges(validPathInfos).forEach((edgeInfo) => {
        const rulerHash = genLineSegHash(edgeInfo.start, edgeInfo.end);
        if (existingRulerHashSet.has(rulerHash)) return;
        existingRulerHashSet.add(rulerHash);
        regionEdges.push({
          id: `region-${edgeInfo.sepId || edgeInfo.wallId || ''}-${rulerHash}`,
          start: edgeInfo.start,
          end: edgeInfo.end
        });
      });
    }
  }

  // 自由普通隔墙标尺
  globalStores.separateWallStore
    .getSepDatas({ onlyNotBase: true })
    .filter((sep) => !connectedSepWallIdSet.has(sep.id) && !sep.center)
    .forEach((sepData) => {
      regionEdges.push({ id: `sep-${sepData.id}`, start: sepData.start, end: sepData.end });
    });

  // 弧形隔墙标尺
  globalStores.separateWallStore.getSepDatas({ onlyNotBase: true }).forEach((sepWallData) => {
    if (!isArcSeparateWallData(sepWallData)) return;

    const _arcRulerInfos = getArcWallRulerInfos({
      start: sepWallData.start,
      end: sepWallData.end,
      center: sepWallData.center,
      clockwise: sepWallData.clockwise
    });

    let offset: number | undefined;

    // 如果弧形隔墙两点都在同一个房间直墙上，弦长标尺就会生成在房间外，需要额外偏移
    if (connectedSepWallIdSet.has(sepWallData.id)) {
      const roomData = globalStores.getDataByIdType(sepWallData.relateRoomId, EEntityType.Room);
      if (roomData) {
        const edges = [roomData.pathInfos, ...roomData.holeInfos].flat();
        for (const edge of edges) {
          if (isArcWallEdge(edge)) {
            continue;
          }
          const lineSeg = new LineSeg2d(edge.start, edge.end);
          if (lineSeg.pointOnSegment(sepWallData.start) && lineSeg.pointOnSegment(sepWallData.end)) {
            offset = -DEFAULT_RULER_OFFSET;
            break;
          }
        }
      }
    }

    arcRulerInfos.push(
      { ..._arcRulerInfos.sagittaRuler, id: `${sepWallData.id}-${_arcRulerInfos.sagittaRuler.id}` },
      { ..._arcRulerInfos.chordRuler, id: `${sepWallData.id}-${_arcRulerInfos.chordRuler.id}`, offset: offset }
    );

    arcChordGuideLines.push(_arcRulerInfos.chordRuler);
  });

  return { rulerInfos: [...wallEdges, ...regionEdges, ...arcRulerInfos], chordGuideLines: arcChordGuideLines };
}

function isArcWallEdge(
  wallEdge: IWallEdge
): wallEdge is Omit<IWallEdge, 'center' | 'clockwise'> & Required<Pick<IWallEdge, 'center' | 'clockwise'>> {
  return wallEdge.center !== undefined && wallEdge.clockwise !== undefined;
}
